import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../../services/api";

const StudentProgressOne = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStudentsWithProgress = async () => {
      try {
        // First, get a list of all users
        const usersResponse = await api.users.getAll();

        if (!usersResponse.success) {
          throw new Error(usersResponse.message || "Failed to fetch users");
        }

        // Filter to only include users with role USER or STUDENT
        // and limit to the first 6
        const studentUsers = usersResponse.data
          .filter((user) => user.role === "USER" || user.role === "STUDENT")
          .slice(0, 6);

        if (studentUsers.length === 0) {
          setStudents([]);
          setLoading(false);
          return;
        }

        // For each student, fetch their progress info
        const studentsWithProgress = await Promise.all(
          studentUsers.map(async (student) => {
            try {
              const progressResponse = await api.userProgress.getByUserId(
                student.id
              );

              if (progressResponse.success && progressResponse.data) {
                return {
                  ...student,
                  progress: progressResponse.data.overallProgress || 0,
                  examsTaken: progressResponse.data.totalExamsTaken || 0,
                  examsPassed: progressResponse.data.totalExamsPassed || 0,
                  quizzesTaken: progressResponse.data.totalQuizzesTaken || 0,
                  practiceQuestions:
                    progressResponse.data.totalPracticeQuestions || 0,
                  averageScore: progressResponse.data.averageScore || 0,
                };
              } else {
                // If no progress data found, set default values
                return {
                  ...student,
                  progress: 0,
                  examsTaken: 0,
                  examsPassed: 0,
                  quizzesTaken: 0,
                  practiceQuestions: 0,
                  averageScore: 0,
                };
              }
            } catch (error) {
              console.error(
                `Error fetching progress for user ${student.id}:`,
                error
              );
              return {
                ...student,
                progress: 0,
                examsTaken: 0,
                examsPassed: 0,
                quizzesTaken: 0,
                practiceQuestions: 0,
                averageScore: 0,
              };
            }
          })
        );

        // Sort by progress (highest first)
        const sortedStudents = studentsWithProgress.sort(
          (a, b) => b.progress - a.progress
        );
        setStudents(sortedStudents);
      } catch (error) {
        console.error("Error fetching students with progress:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudentsWithProgress();
  }, []);

  // Default placeholder image if user doesn't have one
  const defaultUserImage = "assets/images/home-six/student-img1.png";

  // Function to generate circle dash offset for progress circle
  const getCircleDashOffset = (percentage) => {
    // The formula depends on your circle's circumference
    const circumference = 2 * Math.PI * 35; // 35 is the radius of your circle
    return ((100 - percentage) / 100) * circumference;
  };

  return (
    <div className="col-xxl-4 col-md-6">
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between">
            <h6 className="mb-2 fw-bold text-lg mb-0">Student's Progress</h6>
            <Link
              to="/users-list"
              className="text-primary-600 hover-text-primary d-flex align-items-center gap-1"
            >
              View All
              <Icon icon="solar:alt-arrow-right-linear" className="icon" />
            </Link>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">Loading students...</div>
          ) : students.length === 0 ? (
            <div className="text-center py-4">No students found</div>
          ) : (
            students.map((student, index) => (
              <div
                key={student.id || index}
                className={`d-flex align-items-center justify-content-between gap-3 ${
                  index < students.length - 1 ? "mb-24" : "mb-0"
                }`}
              >
                <div className="d-flex align-items-center">
                  <img
                    src={student.avatar || defaultUserImage}
                    alt={`${student.firstName} ${student.lastName}`}
                    className="w-40-px h-40-px radius-8 flex-shrink-0 me-12 overflow-hidden"
                  />
                  <div className="flex-grow-1">
                    <h6 className="text-md mb-0 fw-medium">{`${student.firstName} ${student.lastName}`}</h6>
                    <span className="text-sm text-secondary-light fw-medium">
                      {student.practiceQuestions > 0
                        ? `${student.practiceQuestions} questions completed`
                        : "No practice yet"}
                    </span>
                  </div>
                </div>
                <div className="">
                  <span className="text-primary-light text-sm d-block text-end">
                    <svg
                      className="radial-progress"
                      data-percentage={student.progress}
                      viewBox="0 0 80 80"
                    >
                      <circle className="incomplete" cx={40} cy={40} r={35} />
                      <circle
                        className="complete"
                        cx={40}
                        cy={40}
                        r={35}
                        style={{
                          strokeDashoffset: getCircleDashOffset(
                            student.progress
                          ),
                        }}
                      ></circle>
                      <text
                        className="percentage"
                        x="50%"
                        y="57%"
                        transform="matrix(0, 1, -1, 0, 80, 0)"
                      >
                        {student.progress}
                      </text>
                    </svg>
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentProgressOne;
